import React, { useState } from 'react';
import { useRefinementList } from 'react-instantsearch';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface RefinementListProps {
  attribute: string;
  title: string;
  expanded?: boolean;
  limit?: number;
  searchable?: boolean;
  searchablePlaceholder?: string;
  transformItems?: (items: any[]) => any[];
  showMore?: boolean;
  showMoreLimit?: number;
}

export const RefinementList: React.FC<RefinementListProps> = ({
  attribute,
  title,
  expanded = true,
  limit = 20,
  searchable = false,
  searchablePlaceholder = 'Search...',
  transformItems,
  showMore = false,
  showMoreLimit,
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const {
    items,
    refine,
    searchForItems,
    canToggleShowMore,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    attribute,
    limit,
    searchable,
    searchablePlaceholder,
    transformItems,
    showMore,
    showMoreLimit,
  });

  // Only hide component if there are no items AND it's not searchable
  // If searchable, keep the search box visible even with no results
  if (items.length === 0 && !searchable) {
    return null;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto">
          <span className="font-medium">{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              isExpanded && "rotate-180"
            )}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 p-2">
        {searchable && (
          <input
            type="search"
            placeholder={searchablePlaceholder}
            className="w-full px-3 py-2 text-sm border rounded-md"
            onChange={(e) => searchForItems(e.target.value)}
          />
        )}

        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2 px-2">
            No results found.
          </div>
        ) : (
          <>
            <ScrollArea className="max-h-48">
              <div className="space-y-1">
                {items.map((item) => (
                  <div key={item.value} className="flex items-center space-x-2 py-1">
                    <Checkbox
                      id={`${attribute}-${item.value}`}
                      checked={item.isRefined}
                      onCheckedChange={() => refine(item.value)}
                    />
                    <label
                      htmlFor={`${attribute}-${item.value}`}
                      className="flex-1 text-sm cursor-pointer flex justify-between"
                    >
                      <span className="truncate">{item.label}</span>
                      <span className="text-muted-foreground ml-2">({item.count})</span>
                    </label>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {canToggleShowMore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleShowMore}
                className="w-full mt-2"
              >
                {isShowingMore ? 'Show Less' : 'Show More'}
              </Button>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};